import { HeaderTestIds } from '@trello/test-ids';
import classNames from 'classnames';
import { forTemplate } from '@trello/i18n';
import { useBoardShortLink, useOrganizationName } from '@trello/routes';
import { Analytics } from '@trello/atlassian-analytics';
import { hasValidInviteTokenForModel } from '@trello/invitation-tokens';
import { useLocation } from '@trello/router';

import { ScreenBreakpoints } from 'app/src/components/Responsive';
import React, { useCallback, useMemo } from 'react';
import Media from 'react-media';
import { escapeReturnUrl } from '@trello/urls';
import { DesktopPlaceholder } from './DesktopPlaceholder';
import { HeaderLogo } from './HeaderLogo';
import styles from './AnonymousHeader.less';

import { useAnonymousHeaderBoardQuery } from './AnonymousHeaderBoardQuery.generated';
import { useAnonymousHeaderOrganizationQuery } from './AnonymousHeaderOrganizationQuery.generated';

const formatPromo = forTemplate('header_promo_nav');
const formatSignUpLogin = forTemplate('header_signup_login');

enum ModelType {
  Board = 'Board',
  Organization = 'Organization',
}

export const AnonymousHeader: React.FunctionComponent = () => {
  const { pathname } = useLocation();
  const returnUrl = escapeReturnUrl(pathname);
  const boardShortLink = useBoardShortLink();
  const orgName = useOrganizationName();

  const { data: boardData } = useAnonymousHeaderBoardQuery({
    variables: {
      shortLink: boardShortLink || '',
    },
    skip: !boardShortLink,
  });
  const { data: orgData } = useAnonymousHeaderOrganizationQuery({
    variables: {
      orgName: orgName || '',
    },
    skip: !orgName,
  });

  const board = boardData?.board;
  const boardId = board?.id;
  const boardOrgId = board?.idOrganization;
  const org = orgData?.organization;
  const orgId = org?.id;

  const { modelName, analyticsContainers } = useMemo(() => {
    if (boardShortLink) {
      return {
        modelName: ModelType.Board,
        analyticsContainers: {
          board: {
            id: boardId,
          },
          ...(boardOrgId
            ? {
                organization: {
                  id: boardOrgId,
                },
              }
            : {}),
        },
      };
    } else if (orgName) {
      return {
        modelName: ModelType.Organization,
        analyticsContainers: {
          organization: {
            id: orgId,
          },
        },
      };
    }

    return { modelName: '', analyticsContainers: {} };
  }, [boardShortLink, orgName, boardId, boardOrgId, orgId]);

  const onSignupClick = useCallback(() => {
    if (
      modelName === ModelType.Board &&
      board &&
      hasValidInviteTokenForModel(board)
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'headerSignupButton',
        source: 'acceptBoardInvitationScreen',
        containers: analyticsContainers,
      });
    } else if (
      modelName === ModelType.Organization &&
      org &&
      hasValidInviteTokenForModel(org)
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'headerSignupButton',
        source: 'acceptTeamInvitationScreen',
        containers: analyticsContainers,
      });
    }
  }, [modelName, board, org, analyticsContainers]);

  const onLoginClick = useCallback(() => {
    if (
      modelName === ModelType.Board &&
      board &&
      hasValidInviteTokenForModel(board)
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'headerLoginButton',
        source: 'acceptBoardInvitationScreen',
        containers: analyticsContainers,
      });
    } else if (
      modelName === ModelType.Organization &&
      org &&
      hasValidInviteTokenForModel(org)
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'headerLoginButton',
        source: 'acceptTeamInvitationScreen',
        containers: analyticsContainers,
      });
    }
  }, [modelName, board, org, analyticsContainers]);

  return (
    <div
      className={classNames(styles.header, styles.anonymous, styles.noPrint)}
      data-test-id={HeaderTestIds.Container}
    >
      <Media query={ScreenBreakpoints.MediumMin}>
        {(matches: boolean) =>
          matches && (
            <div className={styles.leftSection}>
              <a href="/" className={styles.textLink}>
                {formatPromo('home')}
              </a>
              <a href="/tour" className={styles.textLink}>
                {formatPromo('tour')}
              </a>
              <a href="https://blog.trello.com" className={styles.textLink}>
                {formatPromo('blog')}
              </a>
            </div>
          )
        }
      </Media>
      <Media query={ScreenBreakpoints.MediumMin}>
        {(matches: boolean) => (
          <HeaderLogo anonymous skipRouter={true} isLeftAligned={!matches} />
        )}
      </Media>
      <div className={styles.rightSection}>
        <a
          href={`/signup?returnUrl=${returnUrl}`}
          className={classNames(styles.headerButton, styles.signUpColor)}
          onClick={onSignupClick}
        >
          {formatSignUpLogin('sign-up')}
        </a>
        <a
          href={`/login?returnUrl=${returnUrl}`}
          className={classNames(styles.headerButton, styles.normalColor)}
          onClick={onLoginClick}
        >
          {formatSignUpLogin('log-in')}
        </a>
        <DesktopPlaceholder />
      </div>
    </div>
  );
};
